import type { PropsWithChildren } from "react"
import React, { useMemo, createContext, useContext } from "react"

import type {
    OutfitterFieldsFragment,
    OutfitterListingFieldsFragment,
    WidgetFieldsFragment,
} from "~graphql/generated/graphql"
import useModalState from "~utils/use-modal-state"
import {
    getTriggerSettings,
    type TriggerSettings,
    type WidgetAttributes,
} from "~widget/widget-helpers"

interface WidgetContextValue {
    isModalOpen: boolean
    isWidgetCheckout: boolean
    openModal: () => void
    closeModal: () => void
    widget: WidgetFieldsFragment | null
    outfitter?: OutfitterFieldsFragment
    listings: OutfitterListingFieldsFragment[]
    widgetAttributes: WidgetAttributes
    triggerSettings: TriggerSettings
    targetDivHtml: string
}

interface Props extends PropsWithChildren {
    readonly widget: WidgetFieldsFragment | null
    readonly outfitter: OutfitterFieldsFragment
    readonly listings: OutfitterListingFieldsFragment[]
    readonly widgetAttributes: WidgetAttributes
    readonly targetDivHtml: string
}

const WidgetContext = createContext<WidgetContextValue>({
    isModalOpen: false,
    isWidgetCheckout: false, // Must default to false else when we are outside the provider this will incorrectly say we aren't
    openModal: () => {},
    closeModal: () => {},
    widget: null,
    outfitter: undefined,
    listings: [],
    widgetAttributes: {
        widgetId: null,
        outfitterId: null,
        isAlreadyLoaded: false,
        shouldUseParent: false,
        shouldOverrideModal: false,
        shouldNotLoadHidden: false,
        mobileAlign: null,
        desktopAlign: null,
        isMobileInline: false,
        isDesktopInline: false,
        triggerType: null,
        buttonColor: null,
        textColor: null,
        text: null,
        isExternal: false,
    },
    triggerSettings: {
        triggerType: "",
        buttonColor: "",
        textColor: "",
        text: "",
        isExternal: false,
    },
    targetDivHtml: "",
})

export default function WidgetProvider({
    widget,
    outfitter,
    listings,
    widgetAttributes,
    targetDivHtml,
    children,
}: Props) {
    const modalState = useModalState()
    const triggerSettings = useMemo(
        () => getTriggerSettings({ widget, widgetAttributes }),
        [widget, widgetAttributes]
    )

    const contextValue = useMemo(
        () => ({
            ...modalState,
            widget,
            outfitter,
            listings,
            widgetAttributes,
            triggerSettings,
            targetDivHtml,
            isWidgetCheckout: true,
        }),
        [
            modalState,
            widget,
            outfitter,
            listings,
            widgetAttributes,
            triggerSettings,
        ]
    )

    return (
        <WidgetContext.Provider value={contextValue}>
            {children}
        </WidgetContext.Provider>
    )
}

export function useWidgetContext() {
    return useContext(WidgetContext)
}
